import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";

function formatStepCompletedAt(iso: string | null | undefined): string | null {
  if (iso == null || iso === "") return null;
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

import {
  completeDispensaryOnboarding,
  deriveDispensaryOnboardingStep,
  DISPENSARY_ONBOARDING_FLOW,
  failDispensaryOnboarding,
  fetchDispensaryOnboarding,
  type DispensaryOnboardingStep,
  type OnboardingRecordDto,
} from "../lib/fetch-dispensary-onboarding";
import { fetchDispensaryProfile } from "../lib/fetch-dispensary-profile";
import { useDispensaryWorkspace } from "../workspace/dispensary-workspace-context";

const STEP_LABEL: Record<DispensaryOnboardingStep, string> = {
  kyb_verification: "Business verification (KYB)",
  bank_account_linking: "Bank account",
  fee_schedule_agreement: "Fee schedule",
  completed: "Finish setup",
};

export function DispensaryOnboardingScreen() {
  const { dispensaryId, getToken } = useDispensaryWorkspace();
  const [profileName, setProfileName] = useState<string | null>(null);
  const [profileDone, setProfileDone] = useState<string | null>(null);
  const [records, setRecords] = useState<OnboardingRecordDto[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [bankLast4, setBankLast4] = useState("");
  const [bankName, setBankName] = useState("");
  const [failOpen, setFailOpen] = useState(false);
  const [failReason, setFailReason] = useState("");

  const load = useCallback(async () => {
    if (!dispensaryId) return;
    setLoadError(null);
    try {
      const [p, o] = await Promise.all([
        fetchDispensaryProfile(getToken, dispensaryId),
        fetchDispensaryOnboarding(getToken, dispensaryId),
      ]);
      setProfileName(p.dbaName?.trim() || p.legalName);
      setProfileDone(p.onboardingCompletedAt);
      setRecords(o.records);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
    }
  }, [dispensaryId, getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const derived = useMemo(
    () => deriveDispensaryOnboardingStep(records, profileDone),
    [records, profileDone],
  );

  /** Index of the first incomplete step (server truth). Completed-only review uses indices below this. */
  const serverStep = derived.done ? null : derived.step;
  const serverStepIdx = serverStep == null ? -1 : DISPENSARY_ONBOARDING_FLOW.indexOf(serverStep);
  const [viewIdx, setViewIdx] = useState(0);

  useEffect(() => {
    if (serverStep == null) return;
    setViewIdx(DISPENSARY_ONBOARDING_FLOW.indexOf(serverStep));
  }, [records, profileDone, serverStep]);

  const step = !derived.done ? DISPENSARY_ONBOARDING_FLOW[viewIdx] : null;
  const isReviewingDoneStep = serverStepIdx >= 0 && viewIdx < serverStepIdx;
  const canGoBack = viewIdx > 0;
  const canGoForward = serverStepIdx >= 0 && viewIdx < serverStepIdx;

  const onComplete = async (step: DispensaryOnboardingStep, extra?: Record<string, unknown>) => {
    if (!dispensaryId) return;
    setBusy(true);
    setActionError(null);
    try {
      const body: Parameters<typeof completeDispensaryOnboarding>[2] = {
        step,
        metadata: { source: "dispensary_web", ...extra },
      };
      if (step === "bank_account_linking") {
        body.bankAccountLast4 = bankLast4.trim() || null;
        body.bankName = bankName.trim() || null;
      }
      await completeDispensaryOnboarding(getToken, dispensaryId, body);
      setBankLast4("");
      setBankName("");
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onFail = async () => {
    if (!dispensaryId || serverStep == null || viewIdx !== serverStepIdx || !failReason.trim()) return;
    setBusy(true);
    setActionError(null);
    try {
      await failDispensaryOnboarding(getToken, dispensaryId, {
        step: serverStep,
        failureReason: failReason.trim(),
      });
      setFailReason("");
      setFailOpen(false);
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!dispensaryId) {
    return (
      <div className="space-y-3">
        <h1 className="text-2xl font-semibold text-ink">Onboarding</h1>
        <p className="text-sm text-gray-600">
          Set a dispensary scope (platform admin) or use an account linked to a dispensary to continue.
        </p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-3">
        <h1 className="text-2xl font-semibold text-ink">Onboarding</h1>
        <p className="text-sm text-red">{loadError}</p>
        <button
          type="button"
          className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-ink hover:border-green-hover"
          onClick={() => void load()}
        >
          Retry
        </button>
      </div>
    );
  }

  if (derived.done) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-ink">Onboarding</h1>
        <div className="rounded-lg border border-green/30 bg-green/5 px-4 py-3 text-sm text-ink">
          <p className="font-medium text-green">You&apos;re all set</p>
          <p className="mt-1 text-gray-600">
            {profileName ? <span className="font-medium">{profileName}</span> : "This dispensary"} has completed
            onboarding.
          </p>
        </div>
        <Link
          to="/workspace/orders"
          className="inline-block rounded-md bg-green px-4 py-2 text-sm font-medium text-white hover:bg-green-hover"
        >
          Back to orders
        </Link>
      </div>
    );
  }

  if (!step) {
    return null;
  }

  const failedReason = !derived.done ? derived.failedReason : null;
  const recordForView = records.find((r) => r.step === step);

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-ink">Dispensary onboarding</h1>
        {profileName ? (
          <p className="mt-1 text-sm text-gray-600">
            <span className="font-medium text-ink">{profileName}</span>
          </p>
        ) : null}
      </div>

      <ol className="flex flex-wrap gap-2">
        {DISPENSARY_ONBOARDING_FLOW.map((s, i) => {
          const done = records.some((r) => r.step === s && r.completedAt);
          const reachable = i <= serverStepIdx;
          const active = i === viewIdx;
          return (
            <li key={s}>
              <button
                type="button"
                disabled={!reachable}
                onClick={() => reachable && setViewIdx(i)}
                title={reachable ? `Go to ${STEP_LABEL[s]}` : "Complete earlier steps first"}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                  done
                    ? "bg-green/15 text-green"
                    : active
                      ? "bg-ink text-white"
                      : "bg-gray-100 text-gray-500"
                } ${reachable && !active ? "cursor-pointer hover:bg-gray-200" : ""}`}
              >
                {i + 1}. {STEP_LABEL[s]}
              </button>
            </li>
          );
        })}
      </ol>

      {failedReason && viewIdx === serverStepIdx ? (
        <div className="rounded-md border border-amber bg-amber-bg px-4 py-3 text-sm text-ink">
          <p className="font-medium">Previous attempt</p>
          <p className="mt-1 text-gray-700">{failedReason}</p>
        </div>
      ) : null}

      <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <h2 className="text-lg font-semibold text-ink">{STEP_LABEL[step]}</h2>
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              disabled={!canGoBack}
              onClick={() => setViewIdx((j) => j - 1)}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-ink hover:border-green-hover disabled:cursor-not-allowed disabled:opacity-40"
            >
              Back
            </button>
            <button
              type="button"
              disabled={!canGoForward}
              onClick={() => canGoForward && setViewIdx((j) => j + 1)}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-ink hover:border-green-hover disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>

        {isReviewingDoneStep ? (
          <div className="mt-4 rounded-md border border-green/25 bg-green/5 px-4 py-3 text-sm text-gray-700">
            <p className="font-medium text-green">This step is complete</p>
            {recordForView?.completedAt ? (
              <p className="mt-1 text-gray-600">
                Completed {formatStepCompletedAt(recordForView.completedAt) ?? recordForView.completedAt}
              </p>
            ) : (
              <p className="mt-1 text-gray-600">You can review earlier steps or continue forward.</p>
            )}
          </div>
        ) : null}

        {!isReviewingDoneStep && step === "kyb_verification" ? (
          <div className="mt-4 space-y-3 text-sm text-gray-700">
            <p>
              Confirm your retail business is verified for Colorado cannabis delivery (license on file, good standing).
              Platform ops may request documents out of band.
            </p>
            <button
              type="button"
              disabled={busy}
              className="rounded-md bg-green px-4 py-2 text-sm font-medium text-white hover:bg-green-hover disabled:opacity-50"
              onClick={() => void onComplete("kyb_verification")}
            >
              Mark KYB verified
            </button>
          </div>
        ) : null}

        {!isReviewingDoneStep && step === "bank_account_linking" ? (
          <div className="mt-4 space-y-4 text-sm text-gray-700">
            <p>
              Link a bank account for payouts (Plaid). For development you can save last4 and bank name only; production
              flows should complete Plaid Link and send tokens.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-xs">
                <span className="text-gray-500">Account last 4</span>
                <input
                  value={bankLast4}
                  onChange={(e) => setBankLast4(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  className="mt-1 w-full rounded border border-gray-200 px-3 py-2 font-mono text-sm text-ink"
                  placeholder="1234"
                  maxLength={4}
                />
              </label>
              <label className="block text-xs">
                <span className="text-gray-500">Bank name</span>
                <input
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  className="mt-1 w-full rounded border border-gray-200 px-3 py-2 text-sm text-ink"
                  placeholder="e.g. Chase"
                />
              </label>
            </div>
            <button
              type="button"
              disabled={busy}
              className="rounded-md bg-green px-4 py-2 text-sm font-medium text-white hover:bg-green-hover disabled:opacity-50"
              onClick={() => void onComplete("bank_account_linking")}
            >
              Save and continue
            </button>
          </div>
        ) : null}

        {!isReviewingDoneStep && step === "fee_schedule_agreement" ? (
          <div className="mt-4 space-y-3 text-sm text-gray-700">
            <p>
              Platform and delivery fees are defined in your contract and fee schedules. Continuing confirms you accept
              the current fee terms for this dispensary.
            </p>
            <button
              type="button"
              disabled={busy}
              className="rounded-md bg-green px-4 py-2 text-sm font-medium text-white hover:bg-green-hover disabled:opacity-50"
              onClick={() => void onComplete("fee_schedule_agreement")}
            >
              Accept fee schedule
            </button>
          </div>
        ) : null}

        {!isReviewingDoneStep && step === "completed" ? (
          <div className="mt-4 space-y-3 text-sm text-gray-700">
            <p>Finalize onboarding to mark this dispensary as ready for operations in the platform.</p>
            <button
              type="button"
              disabled={busy}
              className="rounded-md bg-green px-4 py-2 text-sm font-medium text-white hover:bg-green-hover disabled:opacity-50"
              onClick={() => void onComplete("completed")}
            >
              Complete onboarding
            </button>
          </div>
        ) : null}

        {actionError ? <p className="mt-2 text-sm text-red">{actionError}</p> : null}
      </section>

      <div className="border-t border-gray-200 pt-4">
        <button
          type="button"
          disabled={viewIdx !== serverStepIdx}
          className="text-sm text-gray-500 underline hover:text-ink disabled:cursor-not-allowed disabled:no-underline disabled:opacity-40"
          onClick={() => setFailOpen((v) => !v)}
        >
          Report a problem with this step
        </button>
        {failOpen && viewIdx === serverStepIdx ? (
          <div className="mt-3 space-y-2">
            <textarea
              value={failReason}
              onChange={(e) => setFailReason(e.target.value)}
              className="min-h-[88px] w-full rounded border border-gray-200 px-3 py-2 text-sm text-ink"
              placeholder="What went wrong?"
            />
            <button
              type="button"
              disabled={busy || !failReason.trim()}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-ink hover:bg-gray-50 disabled:opacity-50"
              onClick={() => void onFail()}
            >
              Submit issue
            </button>
          </div>
        ) : null}
      </div>

      <p className="text-xs text-gray-500">
        Viewing step {viewIdx + 1} of {DISPENSARY_ONBOARDING_FLOW.length}
        {serverStepIdx >= 0 ? (
          <span className="text-gray-400"> — current work is step {serverStepIdx + 1}</span>
        ) : null}
      </p>
    </div>
  );
}
